import { useState, useEffect, useCallback, RefObject, useRef } from 'react';
import { SectionDef } from './types';

interface UseOverflowTabsArgs {
  /** Ref to the container element */
  containerRef: RefObject<HTMLElement>;

  /** All enabled sections */
  sections: SectionDef[];

  /** Width to reserve for "More" button (default: 80px) */
  moreButtonWidth?: number;
}

interface UseOverflowTabsResult {
  /** Sections that fit in visible area */
  visible: SectionDef[];

  /** Sections that overflow to "More" menu */
  overflow: SectionDef[];
}

/**
 * Calculates which tabs fit in container and which overflow
 *
 * Uses ResizeObserver for dynamic recalculation on window resize
 * Prioritizes tabs by priority value (lower = more important)
 */
export function useOverflowTabs({
  containerRef,
  sections,
  moreButtonWidth = 80,
}: UseOverflowTabsArgs): UseOverflowTabsResult {
  const [visible, setVisible] = useState<SectionDef[]>([]);
  const [overflow, setOverflow] = useState<SectionDef[]>([]);

  // Use refs to track latest values without causing dependency loops
  const sectionsRef = useRef<SectionDef[]>(sections);
  const moreButtonWidthRef = useRef(moreButtonWidth);

  // Update refs synchronously (no useEffect needed)
  sectionsRef.current = sections;
  moreButtonWidthRef.current = moreButtonWidth;

  /**
   * Estimates width of a tab item
   *
   * Formula: icon (24px) + gap (8px) + label (estimated) + padding (32px) + badge (24px if present)
   * This is a heuristic; for pixel-perfect results, use getBoundingClientRect() on rendered elements
   */
  const estimateTabWidth = useCallback((section: SectionDef): number => {
    const iconWidth = 24;
    const gapWidth = 8;
    const paddingWidth = 32; // MUI Tab horizontal padding (16px each side)
    const badgeWidth = section.badgeCount !== undefined ? 24 : 0;

    // Estimate label width: ~7px per character (more accurate for typical fonts)
    // Use a minimum of 60px for short labels
    const labelWidth = Math.max(60, section.label.length * 7);

    return iconWidth + gapWidth + labelWidth + paddingWidth + badgeWidth;
  }, []);

  /**
   * Recomputes visible vs overflow tabs based on container width
   * Uses refs to read latest values without causing dependency loops
   */
  const recompute = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const maxWidth = container.clientWidth;
    const currentSections = sectionsRef.current;
    const currentMoreButtonWidth = moreButtonWidthRef.current;

    // Sort sections by priority (lower priority number = higher importance)
    const sorted = [...currentSections].sort((a, b) => a.priority - b.priority);

    let accumulatedWidth = 0;
    const visibleTabs: SectionDef[] = [];
    const overflowTabs: SectionDef[] = [];

    for (const section of sorted) {
      const tabWidth = estimateTabWidth(section);

      // Check if this tab + "More" button would fit
      if (accumulatedWidth + tabWidth + currentMoreButtonWidth <= maxWidth) {
        visibleTabs.push(section);
        accumulatedWidth += tabWidth;
      } else {
        overflowTabs.push(section);
      }
    }

    // If nothing overflows, we don't need to reserve space for "More" button
    // Recalculate to potentially fit one more tab
    if (overflowTabs.length === 0 && sorted.length > visibleTabs.length) {
      const remaining = sorted.slice(visibleTabs.length);
      for (const section of remaining) {
        const tabWidth = estimateTabWidth(section);
        if (accumulatedWidth + tabWidth <= maxWidth) {
          visibleTabs.push(section);
          accumulatedWidth += tabWidth;
        } else {
          overflowTabs.push(section);
        }
      }
    }

    setVisible(visibleTabs);
    setOverflow(overflowTabs);
  }, [containerRef, estimateTabWidth]);

  /**
   * Recompute when section count or moreButtonWidth changes
   * (not when section array reference changes, which happens every render)
   */
  useEffect(() => {
    recompute();
  }, [sections.length, moreButtonWidth, recompute]);

  /**
   * Set up ResizeObserver to recalculate on container size changes
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to debounce and avoid layout thrashing
      requestAnimationFrame(recompute);
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once - recompute reads from refs for latest values

  return { visible, overflow };
}
